
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useEtsyStore } from "@/hooks/useEtsyStore";

interface DashboardStats {
  totalOrders: number;
  totalRevenue: number;
  totalRevenueByCurrency: Record<string, number>;
  totalProducts: number;
  totalSales: number;
  recentOrders: Array<{
    id: string;
    receiptId: string;
    customerName: string;
    productName: string;
    status: string;
    totalAmount: number;
    currencyCode: string;
    date: string;
  }>;
}

export const useDashboardStats = () => {
  const { user } = useAuth();
  const {
    isConnected,
    hasActiveSubscription,
    orders,
    products,
  } = useEtsyStore();

  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    totalRevenue: 0,
    totalRevenueByCurrency: {},
    totalProducts: 0,
    totalSales: 0,
    recentOrders: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isConnected) {
      setStats({
        totalOrders: 0,
        totalRevenue: 0,
        totalRevenueByCurrency: {},
        totalProducts: 0,
        totalSales: 0,
        recentOrders: []
      });
    }
  }, [isConnected]);

  useEffect(() => {
    const calculateStats = () => {
      if (!user || !hasActiveSubscription) {
        setStats({
          totalOrders: 0,
          totalRevenue: 0,
          totalRevenueByCurrency: {},
          totalProducts: 0,
          totalSales: 0,
          recentOrders: []
        });
        setIsLoading(false);
        return;
      }

      // Calculate total revenue by currency from Etsy orders
      const totalRevenueByCurrency = orders.reduce((acc, order) => {
        const currency = order.currencyCode || 'USD';
        const amount = Number(order.totalAmount) || 0;
        if (!acc[currency]) {
          acc[currency] = 0;
        }
        acc[currency] = Number(acc[currency]) + amount;
        return acc;
      }, {} as Record<string, number>);

      const totalRevenue = Object.values(totalRevenueByCurrency).reduce((sum, amount) => {
        return sum + Number(amount);
      }, 0);

      // Calculate total sales from products
      const totalSales = products.reduce((sum, product) => {
        return sum + (Number(product.salesCount) || 0);
      }, 0);

      // Get recent orders (last 5)
      const recentOrders = orders
        .slice(0, 5)
        .map(order => ({
          id: order.receiptId,
          receiptId: order.receiptId,
          customerName: order.customerName,
          productName: order.productName,
          status: order.status,
          totalAmount: Number(order.totalAmount) || 0,
          currencyCode: order.currencyCode || 'USD',
          date: order.date
        }));

      setStats({
        totalOrders: orders.length,
        totalRevenue,
        totalRevenueByCurrency,
        totalProducts: products.length,
        totalSales,
        recentOrders
      });
      
      setIsLoading(false);
    };

    calculateStats();
  }, [orders, products, user, hasActiveSubscription]);

  return { stats, isLoading };
};
